import { Injectable } from '@angular/core';
import { CompoundFilter } from '@itonics/filters';

import { normalize } from '../../shared/helpers/normalize-helper';
import { ASYNC_METRIC_OPTIONS, MetricOptions } from '../../shared/model/metric';
import { DocumentsQueryAggregation, ExecutionDetailAsync, SignalsDictionary, SignalsType } from '../../shared/model/types';
import { ApiService } from '../../shared/modules/shared-services/services/api.service';
import { MetricService } from '../../shared/modules/shared-services/services/metric.service';
import { PropertiesService } from '../../shared/modules/shared-services/services/properties.service';

@Injectable({
  providedIn: 'root'
})
export class WorldMapService {
  constructor(
    private readonly apiService: ApiService,
    private readonly propertiesService: PropertiesService,
    private readonly metricService: MetricService
  ) {}

  /**
   *
   * @param query filter applied
   * @param id If provided will replace all pending previous calls with the same ID
   * @returns Promise<Array<DocumentsQueryAggregation>>
   */
  public async getWorldMapAggregations(
    query: CompoundFilter,
    metricOptions: MetricOptions,
    id?: string
  ): Promise<Array<DocumentsQueryAggregation>> {
    // Certain metric options should be requested asynchronously because calculating
    // their results will otherwise cause a synchronous request to timeout
    const async = ASYNC_METRIC_OPTIONS.indexOf(metricOptions?.metric) !== -1;

    const postBody = {
      aggregations: [
        {
          property: SignalsType.COUNTRY,
          childAggregations: [
            {
              property: SignalsType.DOCUMENT_TYPE,
              ...metricOptions
            }
          ],
          returnMissingValuesAs: '',
          ...metricOptions
        }
      ],
      query: query.toJson(true),
      limit: 0,
      executionDetails: async ? { asynchronous: ExecutionDetailAsync.Aggregations } : null
    };

    const dictionary = this.propertiesService.getPropertyDictionary(SignalsType.COUNTRY);

    return this.apiService.postDocumentsQuery(postBody, id).then((res) => {
      const countriesAggregation = res.aggregation[SignalsType.COUNTRY];
      const valuesList = countriesAggregation.map((d) => d.value);
      const maxValueCountriesAggregation = Math.max(...valuesList);
      const minValueCountriesAggregation = Math.min(...valuesList);

      return countriesAggregation.reduce((collection, aggregation) => {
        const typeAggregation = aggregation.childAggregations[SignalsType.DOCUMENT_TYPE];

        let normalizeFnc;
        if (this.metricService.isMetricLikeRelevance(metricOptions.metric)) {
          // we normalize by the highest value per category
          normalizeFnc = (value) => normalize(minValueCountriesAggregation, maxValueCountriesAggregation, value);
        }

        return collection.concat(
          typeAggregation
            .map((type) => this.buildWorldMapData(aggregation.key as string, type, normalizeFnc, dictionary))
            // We remove no the (no value) aggregation, so don't bias the bubbles
            // but after we normalized so we have the same values that we have in filters
            .filter((d) => d.country !== '')
        );
      }, []);
    });
  }

  /**
   *
   * @param country the country in abbreviation
   * @param item the aggregation
   * @param normalizeFnc function to normalize values, like for example when we want to get relevance values
   * @param dictionary dictionary for countries
   * @returns  formatted aggregation for worldMap
   */
  private buildWorldMapData(
    country: string,
    item: DocumentsQueryAggregation,
    normalizeFnc: (value: number) => number,
    dictionary?: SignalsDictionary
  ): any {
    const key = item.key as string;
    return {
      country,
      value: normalizeFnc ? normalizeFnc(item.value) : item.value,
      category: key,
      label: dictionary ? dictionary[country] : country,
      valueCount: item.value
    };
  }
}
